import { useI18n } from "@core/i18n/i18n";
import styled from "@emotion/styled";
import { ErrorMessage } from "@ui/components/common/error-message";
import { PasswordInput } from "@ui/components/inputs/text-input";
import { isNotEmpty, isNotNil } from "@utils/assert";
import { classNames } from "primereact/utils";
import React, { ReactNode } from "react";
import { Control, Controller } from "react-hook-form";

const hasCorrectLength = (value: string) => value.length >= 12;
const hasNumber = (value: string) => new RegExp(/\d/).test(value);
const hasUpperCaseLetter = (value: string) => new RegExp(/[A-Z]/).test(value);
const hasLowerCaseLetter = (value: string) => new RegExp(/[a-z]/).test(value);
const hasSpecialCharacter = (value: string) => new RegExp(/[\^$*.[\]{}()?\-"!@#%&/,><':;|_~`]/).test(value);

interface PasswordFieldProps {
	name: string;
	control: unknown;
	error: boolean;
	label?: string;
	labelStyle?: React.CSSProperties;
	rulesStyle?: React.CSSProperties;
	placeholder?: string;
	additionalPasswordValidation?: (password: string) => string | undefined;
	dataTest?: string;
}

export const PasswordField = ({
	additionalPasswordValidation,
	control,
	error,
	name,
	label,
	labelStyle,
	rulesStyle,
	placeholder,
	dataTest,
}: PasswordFieldProps): JSX.Element => {
	const { format } = useI18n();

	return (
		<Controller
			name={name}
			control={control as Control<{ [key: string]: string }, object>}
			rules={{
				required: format("passwordField.password.requiredError"),
				validate: (value: string) => {
					const result = additionalPasswordValidation?.(value);
					if (isNotNil(result)) {
						return result;
					}
					if (
						!hasCorrectLength(value) ||
						!hasNumber(value) ||
						!hasUpperCaseLetter(value) ||
						!hasLowerCaseLetter(value) ||
						!hasSpecialCharacter(value)
					) {
						return format("passwordField.password.incorrectRuleError");
					}
				},
			}}
			render={({ field, fieldState }) => (
				<>
					<label style={labelStyle} className="p-size-m">
						{isNotEmpty(label) ? label : format("passwordField.create.your.password.label")}
					</label>
					<StyledPasswordInput
						{...field}
						data-test={dataTest}
						placeholder={placeholder ?? format("passwordField.password.placeholder")}
						error={fieldState.invalid || !!error}
					/>
					<ErrorMessage>{fieldState.error?.message}</ErrorMessage>
					<div style={rulesStyle}>
						<span>{format("passwordField.rules.description")}</span>
						<Ul>
							<Li error={fieldState.error && !hasCorrectLength(field.value)} valid={hasCorrectLength(field.value)}>
								{format("passwordField.rules.minLength")}
							</Li>
							<Li error={fieldState.error && !hasNumber(field.value)} valid={hasNumber(field.value)}>
								{format("passwordField.rules.numberIncluded")}
							</Li>
							<Li error={fieldState.error && !hasLowerCaseLetter(field.value)} valid={hasLowerCaseLetter(field.value)}>
								{format("passwordField.rules.lowerCaseLetterIncluded")}
							</Li>
							<Li error={fieldState.error && !hasUpperCaseLetter(field.value)} valid={hasUpperCaseLetter(field.value)}>
								{format("passwordField.rules.upperCaseLetterIncluded")}
							</Li>
							<Li
								error={fieldState.error && !hasSpecialCharacter(field.value)}
								valid={hasSpecialCharacter(field.value)}
							>
								{format("passwordField.rules.specialCharacterIncluded")}
							</Li>
						</Ul>
					</div>
				</>
			)}
		/>
	);
};

const StyledPasswordInput = styled(PasswordInput)`
	margin-top: 8px;
	margin-bottom: 8px;
`;

const Ul = styled.ul`
	margin: 0;
	padding-left: 1.5em;
`;

const Li: React.FC<{ error?: boolean; valid?: boolean; children: ReactNode }> = ({ error, valid, children }) => (
	<li className={classNames({ "p-error": !!error, "p-valid": !!valid })}>{children}</li>
);
